import {
    Phone, Handshake, UserCog, Globe, Image, FileText, BarChart3,
    MapPin, Clock, Users, Share2, Warehouse,
} from "lucide-react";

/**
 * The words for each setup item, and where in Settings it gets fixed.
 *
 * The server (queries/setupCompleteness.js) decides WHICH facts count and
 * whether each is done; this file decides what to call them. That split is
 * why copy can be reworded in a frontend build with no server deploy, and it
 * is the same one /enrichment/summary uses — counts from the API, sentences
 * from the component.
 *
 * EVERY HINT NAMES A CONSEQUENCE, not a field. "Add your logo" is a chore;
 * "buyers see your name as plain text where your logo should be" is a reason.
 * A checklist of chores gets dismissed; a list of things currently going wrong
 * on your own website gets read. Keep new hints to that standard.
 *
 * `section` and `tab` address a destination inside Settings and are handed to
 * requestSettingsSection() — `section` matches Settings.jsx's selectedMenuItem
 * values and `tab` its companyTab values. A key here with no entry in
 * SETUP_ITEMS server-side (or the reverse) is caught by p61.
 */
export const SETUP_ITEM_META = {
    contactDetails: {
        icon: Phone,
        label: "Add your contact phone and email",
        hint: "Your public site's footer and every reply to a website enquiry use these. Your email address is blank until you set it.",
        section: "Company",
        tab: "profile",
        anchor: "company-info-section",
    },
    salesDefaults: {
        icon: Handshake,
        label: "Set your default credit terms",
        hint: "Credit period, down payment and daily penalty. Until these are set, every credit deal is typed from scratch. Zero is a valid answer.",
        section: "Sales",
    },
    storage: {
        icon: Warehouse,
        label: "Say where you keep your stock",
        //Names the consequence, per the rule at the top of this file: the cost of
        //leaving it is a question on every single imported vehicle, asking for a
        //figure that describes a bill the dealership may never receive.
        hint: "Until you do, every imported vehicle is treated as sitting in a bonded warehouse and asks for a daily demurrage rate. If you store stock in your own yard, you pay none.",
        section: "Company",
        tab: "profile",
        anchor: "storage-section",
    },
    staffUsers: {
        icon: UserCog,
        label: "Add accounts for your staff",
        hint: "Everyone sharing one login means the audit trail credits every sale, price change and deletion to the same person.",
        section: "Accounts",
    },

    publicSite: {
        icon: Globe,
        label: "Set your public website address",
        hint: "Without it your catalog domain can't find you, and broadcast messages can't link to a vehicle.",
        section: "Company",
        tab: "profile",
        anchor: "public-site-url-field",
    },
    logo: {
        icon: Image,
        label: "Upload your logo",
        hint: "Visitors see your company name as plain text where your logo should be.",
        section: "Company",
        tab: "profile",
        anchor: "company-logo-section",
    },
    about: {
        icon: FileText,
        label: "Write your mission and history",
        hint: "Your About page currently shows your name over an empty space, and the history section isn't shown at all.",
        section: "Company",
        tab: "profile",
        anchor: "about-info-section",
    },
    aboutStats: {
        icon: BarChart3,
        label: "Add your founding year and team size",
        hint: "These fill the \"Est. 2014 — 11 years\" strip at the top of your About page. It's empty without them.",
        section: "Company",
        tab: "profile",
        anchor: "company-stats-section",
    },
    mainLocation: {
        icon: MapPin,
        label: "Add your main location",
        hint: "Your Contact page prints \"Address unavailable\" to anyone trying to visit you.",
        section: "Company",
        tab: "locations",
    },
    locationDetail: {
        icon: Clock,
        label: "Add opening hours and a map pin",
        hint: "On your main location. Without them there's no map and no hours — and the directions link falls back to searching for your address by name.",
        section: "Company",
        tab: "locations",
    },
    team: {
        icon: Users,
        label: "Introduce your team",
        hint: "The \"Meet Our Team\" section doesn't appear on your About page while this is empty.",
        section: "Company",
        tab: "team",
    },
    socials: {
        icon: Share2,
        label: "Link your social media",
        hint: "Your footer says \"Follow us on social media\" above a blank space.",
        section: "Company",
        tab: "social",
    },
};

/**
 * Group headings, in the order the wizard lists them. Website last: it is the
 * longest group and the one that vanishes wholesale for a dealership without a
 * public site, so leading with it would make the list lurch.
 */
export const SETUP_GROUPS = [
    { key: "contact", label: "Contact details" },
    { key: "sales", label: "Sales defaults" },
    { key: "people", label: "Your team" },
    { key: "website", label: "Your public website" },
];

/**
 * The applicable items, incomplete first.
 *
 * NOTE that the wizard then regroups these under SETUP_GROUPS headings, so
 * incomplete-first holds WITHIN a group, not across the whole modal — a
 * finished "Contact details" group still renders above an outstanding website
 * one. That is deliberate: the ticks are the progress, and hiding them would
 * leave a list that only ever shows what is wrong. Do not write a test that
 * assumes the first row on screen is incomplete.
 *
 * Catalog order is preserved within each half so the list does not reshuffle
 * under the reader as items are ticked off mid-session.
 */
export function orderedItems(items) {
    if (!items) return [];
    const order = Object.keys(SETUP_ITEM_META);
    const applicable = order
        .filter((key) => items[key]?.applicable && SETUP_ITEM_META[key])
        .map((key) => ({ key, ...items[key], meta: SETUP_ITEM_META[key] }));
    return [...applicable.filter((i) => !i.done), ...applicable.filter((i) => i.done)];
}
